import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseclient";
import { useNavigate } from "react-router-dom";

function Profile() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingResumeUrl, setLoadingResumeUrl] = useState(false);
  const navigate = useNavigate();

  const generateResumeUrl = async (storedUrl, userId) => {
    if (!storedUrl) return null;

    setLoadingResumeUrl(true);
    try {
      // Extract file path from the stored URL
      // The URL format is typically: https://[project].supabase.co/storage/v1/object/public/resumes/[user-id]/resume.[ext]
      // Or it might be just the path: [user-id]/resume.[ext]
      let filePath = storedUrl;

      // If it's a full URL, extract the path
      if (storedUrl.includes("/storage/v1/object/public/resumes/")) {
        filePath = storedUrl.split("/storage/v1/object/public/resumes/")[1];
      } else if (storedUrl.includes("/storage/v1/object/sign/resumes/")) {
        filePath = storedUrl
          .split("/storage/v1/object/sign/resumes/")[1]
          .split("?")[0]; // Remove query params
      } else if (storedUrl.includes("/resumes/")) {
        filePath = storedUrl.split("/resumes/")[1].split("?")[0]; // Remove query params if any
      }

      // Clean up the file path (remove any remaining URL parts)
      if (filePath.includes("?")) {
        filePath = filePath.split("?")[0];
      }

      // If we still don't have a valid path and we have the user ID, try to reconstruct it
      if ((filePath.startsWith("http") || !filePath.includes("/")) && userId) {
        // The path should be in format: [user-id]/resume.[ext]
        // Try to extract extension from URL or use default
        const extMatch = storedUrl.match(/resume\.(\w+)/i);
        const ext = extMatch ? extMatch[1] : "pdf";
        filePath = `${userId}/resume.${ext}`;
      }

      // Generate a signed URL (works for both public and private buckets)
      const { data, error } = await supabase.storage
        .from("resumes")
        .createSignedUrl(filePath, 3600); // URL valid for 1 hour

      if (error) {
        console.error("Error generating signed URL:", error);
        console.error("Attempted file path:", filePath);
        console.error("Stored URL:", storedUrl);
        // Fallback to public URL if signed URL fails
        return storedUrl;
      } else {
        return data.signedUrl;
      }
    } catch (err) {
      console.error("Error generating resume URL:", err);
      // Fallback to stored URL
      return storedUrl;
    } finally {
      setLoadingResumeUrl(false);
    }
  };

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);

      // Get current user session
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session?.user) {
        console.error("No user session found:", sessionError);
        navigate("/sign-in");
        return;
      }

      setUser(session.user);

      // Fetch user profile from users table
      const { data: profile, error: profileError } = await supabase
        .from("users")
        .select("*")
        .eq("id", session.user.id)
        .single();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        console.error("Profile error code:", profileError.code);
        console.error("Profile error message:", profileError.message);
        console.error(
          "Profile error details:",
          JSON.stringify(profileError, null, 2),
        );

        // Check if it's a "not found" error - user might not have a profile record yet
        if (
          profileError.code === "PGRST116" ||
          profileError.message?.includes("No rows")
        ) {
          setError("Profile not found. Please complete your profile setup.");
          // Optionally redirect to complete profile
          // navigate("/complete-profile");
        } else {
          setError(
            `Failed to load user profile: ${
              profileError.message || "Unknown error"
            }`,
          );
        }
      } else if (!profile) {
        setError("Profile not found. Please complete your profile setup.");
      } else {
        setUserProfile(profile);
        // Don't generate resume URL here - only generate it when user clicks the button
      }

      // Fetch applied jobs
      const { data: applications, error: applicationsError } = await supabase
        .from("job_applications")
        .select(
          `
          id,
          applied_date,
          jobs (
            id,
            title,
            company,
            location,
            application_url
          )
        `,
        )
        .eq("user_id", session.user.id)
        .order("applied_date", { ascending: false });

      if (applicationsError) {
        console.error("Error fetching applied jobs:", applicationsError);
        setAppliedJobs([]);
      } else {
        // Transform the data to match the expected format
        const formattedJobs =
          applications?.map((app) => ({
            id: app.jobs?.id || app.id,
            title: app.jobs?.title || "Unknown Job",
            company: app.jobs?.company || "Unknown Company",
            location: app.jobs?.location || "Unknown Location",
            applied_date: app.applied_date,
            application_url: app.jobs?.application_url,
          })) || [];
        setAppliedJobs(formattedJobs);
      }

      const { data: savedRows, error: savedError } = await supabase
        .from("saved_jobs")
        .select(
          `
          id,
          created_at,
          jobs (
            id,
            title,
            company,
            location,
            application_url
          )
        `,
        )
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false });

      if (savedError) {
        console.error("Error fetching saved jobs:", savedError);
        setSavedJobs([]);
      } else {
        const formattedSaved =
          savedRows?.map((row) => ({
            saveId: row.id,
            id: row.jobs?.id,
            title: row.jobs?.title || "Unknown Job",
            company: row.jobs?.company || "Unknown Company",
            location: row.jobs?.location || "",
            application_url: row.jobs?.application_url,
            saved_at: row.created_at,
          })) || [];
        setSavedJobs(formattedSaved.filter((j) => j.id));
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load user data");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleRemoveSaved = async (saveId) => {
    try {
      const { error } = await supabase
        .from("saved_jobs")
        .delete()
        .eq("id", saveId);
      if (error) throw error;
      setSavedJobs((prev) => prev.filter((j) => j.saveId !== saveId));
    } catch (err) {
      console.error("Error removing saved job:", err);
      alert("Could not remove this saved job. Please try again.");
    }
  };

  const handleViewResume = async (e) => {
    e.preventDefault();
    if (!userProfile?.resume_url || !user?.id) return;

    // Generate signed URL and open it
    const signedUrl = await generateResumeUrl(userProfile.resume_url, user.id);
    if (signedUrl) {
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } else {
      // Fallback: try to open the stored URL directly
      window.open(userProfile.resume_url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔄</div>
          <div>Loading your profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div style={{ textAlign: "center", color: "red" }}>
          <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>❌</div>
          <div>{error}</div>
          <button
            onClick={() => navigate("/")}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          borderBottom: "1px solid #e9ecef",
          padding: "1rem 0",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "0 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1
            style={{
              color: "#2c3e50",
              fontSize: "1.8rem",
              fontWeight: "600",
              margin: 0,
            }}
          >
            👤 My Profile
          </h1>
          <button
            onClick={() => navigate("/")}
            style={{
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "0.9rem",
            }}
          >
            Back to Jobs
          </button>
        </div>
      </div>

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0 2rem 2rem",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2rem",
            marginBottom: "2rem",
          }}
        >
          {/* Personal Information */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "2rem",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              border: "1px solid #e9ecef",
            }}
          >
            <h2
              style={{
                color: "#2c3e50",
                fontSize: "1.4rem",
                fontWeight: "600",
                marginBottom: "1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              📋 Personal Information
            </h2>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    color: "#6c757d",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    marginBottom: "0.25rem",
                  }}
                >
                  Full Name
                </label>
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #e9ecef",
                    color: "#2c3e50",
                    fontSize: "1rem",
                  }}
                >
                  {userProfile?.full_name || "Not provided"}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    color: "#6c757d",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    marginBottom: "0.25rem",
                  }}
                >
                  Email Address
                </label>
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #e9ecef",
                    color: "#2c3e50",
                    fontSize: "1rem",
                  }}
                >
                  {user?.email || "Not provided"}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    color: "#6c757d",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    marginBottom: "0.25rem",
                  }}
                >
                  City
                </label>
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #e9ecef",
                    color: "#2c3e50",
                    fontSize: "1rem",
                  }}
                >
                  {userProfile?.city || "Not specified"}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    color: "#6c757d",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    marginBottom: "0.25rem",
                  }}
                >
                  Member Since
                </label>
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #e9ecef",
                    color: "#2c3e50",
                    fontSize: "1rem",
                  }}
                >
                  {formatDate(user?.created_at)}
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate("/edit-profile")}
              style={{
                marginTop: "1.5rem",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                padding: "0.75rem 1.5rem",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "0.9rem",
                width: "100%",
              }}
            >
              Edit Profile
            </button>
          </div>

          {/* Resume Section */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "2rem",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              border: "1px solid #e9ecef",
            }}
          >
            <h2
              style={{
                color: "#2c3e50",
                fontSize: "1.4rem",
                fontWeight: "600",
                marginBottom: "1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              📄 Resume
            </h2>

            {userProfile?.resume_url ? (
              <div>
                <div
                  style={{
                    padding: "1rem",
                    backgroundColor: "#e8f5e8",
                    borderRadius: "6px",
                    border: "1px solid #d4edda",
                    marginBottom: "1rem",
                  }}
                >
                  <p
                    style={{
                      color: "#155724",
                      margin: 0,
                      fontSize: "0.9rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    ✅ Resume uploaded successfully
                  </p>
                </div>
                <button
                  onClick={handleViewResume}
                  disabled={loadingResumeUrl}
                  style={{
                    backgroundColor: loadingResumeUrl ? "#6c757d" : "#007bff",
                    color: "white",
                    border: "none",
                    padding: "0.75rem 1.5rem",
                    borderRadius: "6px",
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    cursor: loadingResumeUrl ? "not-allowed" : "pointer",
                  }}
                >
                  {loadingResumeUrl ? "Loading..." : "View Resume"}
                </button>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    padding: "1rem",
                    backgroundColor: "#fff3cd",
                    borderRadius: "6px",
                    border: "1px solid #ffeaa7",
                    marginBottom: "1rem",
                  }}
                >
                  <p
                    style={{
                      color: "#856404",
                      margin: 0,
                      fontSize: "0.9rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    ⚠️ No resume uploaded yet
                  </p>
                </div>
                <button
                  onClick={() => navigate("/edit-profile")}
                  style={{
                    backgroundColor: "#ffc107",
                    color: "#212529",
                    border: "none",
                    padding: "0.75rem 1.5rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "0.9rem",
                  }}
                >
                  Upload Resume
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Saved jobs (apply later) */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e9ecef",
            marginBottom: "2rem",
          }}
        >
          <h2
            style={{
              color: "#2c3e50",
              fontSize: "1.4rem",
              fontWeight: "600",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            Saved jobs
          </h2>

          {savedJobs.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "400px",
                paddingRight: "0.5rem",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e0 #f1f5f9",
              }}
            >
              {savedJobs.map((job) => (
                <div
                  key={job.saveId}
                  style={{
                    width: "100%",
                    padding: "1rem",
                    backgroundColor: "#fff5f5",
                    borderRadius: "8px",
                    border: "1px solid #f8d7da",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: "200px" }}>
                      <h3
                        style={{
                          color: "#2c3e50",
                          fontSize: "1.1rem",
                          fontWeight: "600",
                          margin: "0 0 0.5rem 0",
                        }}
                      >
                        {job.title}
                      </h3>
                      <p
                        style={{
                          color: "#007bff",
                          fontWeight: "500",
                          margin: "0 0 0.5rem 0",
                        }}
                      >
                        {job.company}
                      </p>
                      {job.location ? (
                        <p
                          style={{
                            color: "#6c757d",
                            fontSize: "0.85rem",
                            margin: "0 0 0.5rem 0",
                          }}
                        >
                          📍 {job.location}
                        </p>
                      ) : null}
                      <p
                        style={{
                          color: "#6c757d",
                          fontSize: "0.9rem",
                          margin: 0,
                        }}
                      >
                        Saved on {formatDate(job.saved_at)}
                      </p>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        alignItems: "stretch",
                      }}
                    >
                      {job.application_url ? (
                        <button
                          type="button"
                          onClick={() =>
                            window.open(
                              job.application_url,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                          style={{
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            padding: "0.5rem 1rem",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "500",
                            fontSize: "0.85rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Apply now
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleRemoveSaved(job.saveId)}
                        style={{
                          backgroundColor: "white",
                          color: "#6c757d",
                          border: "1px solid #ced4da",
                          padding: "0.5rem 1rem",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "500",
                          fontSize: "0.85rem",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "2rem 1rem",
                color: "#6c757d",
              }}
            >
              <p style={{ margin: 0 }}>
                Heart jobs on the board to keep them here and apply when you are
                ready.
              </p>
              <button
                type="button"
                onClick={() => navigate("/")}
                style={{
                  marginTop: "1rem",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  padding: "0.75rem 1.5rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "0.9rem",
                }}
              >
                Browse jobs
              </button>
            </div>
          )}
        </div>

        {/* Applied Jobs Section */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e9ecef",
          }}
        >
          <h2
            style={{
              color: "#2c3e50",
              fontSize: "1.4rem",
              fontWeight: "600",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            📋 Applied Jobs
          </h2>

          {appliedJobs.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "400px",
                paddingRight: "0.5rem",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e0 #f1f5f9",
              }}
            >
              {appliedJobs.map((job) => (
                <div
                  key={job.id}
                  style={{
                    width: "100%",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                    cursor: job.application_url ? "pointer" : "default",
                    transition: "background-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onClick={() => {
                    if (job.application_url) {
                      window.open(job.application_url, "_blank");
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (job.application_url) {
                      e.currentTarget.style.backgroundColor = "#e9ecef";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (job.application_url) {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                >
                  <h3
                    style={{
                      color: "#2c3e50",
                      fontSize: "1.1rem",
                      fontWeight: "600",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    {job.title}
                  </h3>
                  <p
                    style={{
                      color: "#007bff",
                      fontWeight: "500",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    {job.company}
                  </p>
                  {job.location && (
                    <p
                      style={{
                        color: "#6c757d",
                        fontSize: "0.85rem",
                        margin: "0 0 0.5rem 0",
                      }}
                    >
                      📍 {job.location}
                    </p>
                  )}
                  <p
                    style={{
                      color: "#6c757d",
                      fontSize: "0.9rem",
                      margin: 0,
                    }}
                  >
                    Applied on {formatDate(job.applied_date)}
                  </p>
                  {job.application_url && (
                    <p
                      style={{
                        color: "#007bff",
                        fontSize: "0.85rem",
                        margin: "0.5rem 0 0 0",
                        fontStyle: "italic",
                      }}
                    >
                      Click to view job posting →
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "3rem 1rem",
                color: "#6c757d",
              }}
            >
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📝</div>
              <h3 style={{ marginBottom: "0.5rem", color: "#495057" }}>
                No applications yet
              </h3>
              <p style={{ marginBottom: "1.5rem" }}>
                Start applying to jobs to see your application history here.
              </p>
              <button
                onClick={() => navigate("/")}
                style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  padding: "0.75rem 1.5rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "0.9rem",
                }}
              >
                Browse Jobs
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
